import React from 'react';
import { ArrowLeft, Award, Users, Lightbulb, Target, BookOpen, Sparkles, Play, GraduationCap, Handshake, ClipboardList, Calendar, Clock, UserCheck, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import Header from '../components/Header';
import Footer from '../components/Footer';

const ALAA = () => {
  const features = [
    {
      icon: GraduationCap,
      title: 'Expert-Facilitated Workshops',
      description: 'Monthly half-day in-person workshops led by sector experts, combining content with practice-based activities that participants apply directly to their work.',
      gradient: 'from-amber-500 to-orange-500'
    },
    {
      icon: Users,
      title: 'Individual and Group Mentorship',
      description: 'Monthly 1:1 sessions paired with small group meetings that turn real workplace challenges into structured learning through peer and mentor feedback.',
      gradient: 'from-rose-500 to-pink-500'
    },
    {
      icon: Target,
      title: 'Applied Practice',
      description: 'A self-directed stretch goal and real-world case studies that push participants beyond their current scope and build analytical, decision-making, and systems-thinking skills.',
      gradient: 'from-teal-500 to-emerald-500'
    },
    {
      icon: BookOpen,
      title: 'Professional Portfolio',
      description: 'A documented record of each participant\'s leadership journey, including reflections and evidence of growth that participants carry forward into future roles.',
      gradient: 'from-violet-500 to-purple-500'
    }
  ];

  const curriculum = [
    'Foundations of Leadership in Early Childhood',
    'Effective Communication for Leaders',
    'Conflict Resolution in Early Childhood Settings',
    'Emotional Intelligence and Self-Awareness',
    'Decision-Making Protocols for Leaders',
    'Ethical Leadership and Equity'
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section id="main-content" role="main" tabIndex={-1} className="relative pt-20 pb-16 bg-gradient-to-br from-amber-600 via-orange-600 to-rose-700">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 left-10 w-64 h-64 bg-white/10 rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-amber-300/10 rounded-full blur-3xl" />
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16">
          <Link to="/" className="inline-flex items-center gap-2 text-amber-200 hover:text-white mb-8 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
          
          <div className="max-w-4xl">
            <span className="inline-block px-4 py-2 bg-white/20 text-white rounded-full text-sm font-semibold mb-6">
              DCELC &amp; Educare DC Partnership
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6" data-testid="alaa-page-title">
              Maurice Sykes Emerging Leaders Program
            </h1>
            <p className="text-xl text-amber-100 mb-8 max-w-3xl">
              A nine-month leadership development experience for early childhood professionals in the District of Columbia
            </p>
          </div>
        </div>
      </section>

      {/* Program Introduction */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-lg text-slate-700 leading-relaxed">
            The Maurice Sykes Emerging Leaders Program is a nine-month leadership development experience for early childhood professionals in the District of Columbia. Designed in partnership by Educare Washington, DC and the DC Early Learning Collaborative, the program prepares emerging leaders to step into formal leadership roles, equipped with the skills, mindset, and strategic orientation needed to lead equity-centered early childhood programs and systems.
          </p>
        </div>
      </section>

      {/* Why Emerging Leaders Matter */}
      <section className="py-16 bg-gradient-to-br from-amber-50 to-orange-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="w-16 h-16 bg-amber-100 rounded-2xl flex items-center justify-center mb-6">
                <Lightbulb className="w-8 h-8 text-amber-600" />
              </div>
              <h2 className="text-3xl font-bold text-slate-800 mb-6">Why Emerging Leaders Matter</h2>
              <p className="text-slate-700 leading-relaxed">
                Across the District and the nation, talented educators are stepping into leadership roles without preparation in supervision, decision-making, systems thinking, or equity-centered practice. Programs lose great teachers because there is no clear pathway forward. The Maurice Sykes Emerging Leaders Program carries forward the legacy of Maurice Sykes and the Advanced Leadership Advocacy Academy, developing leaders before the role demands them.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white p-6 rounded-2xl shadow-lg border border-amber-100">
                <p className="text-3xl font-bold text-amber-600 mb-1">6 Months</p>
                <p className="text-slate-600 text-sm">Cohort Duration</p>
              </div>
              <div className="bg-white p-6 rounded-2xl shadow-lg border border-amber-100">
                <p className="text-3xl font-bold text-amber-600 mb-1">6 Leaders</p>
                <p className="text-slate-600 text-sm">Inaugural Pilot Cohort</p>
              </div>
              <div className="bg-white p-6 rounded-2xl shadow-lg border border-amber-100">
                <p className="text-3xl font-bold text-amber-600 mb-1">10-12 Hrs</p>
                <p className="text-slate-600 text-sm">Monthly Time Commitment</p>
              </div>
              <div className="bg-white p-6 rounded-2xl shadow-lg border border-amber-100">
                <p className="text-3xl font-bold text-amber-600 mb-1">2-4 Years</p>
                <p className="text-slate-600 text-sm">to Formal Leadership</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Program Features */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-slate-800 mb-4 text-center">Program Features</h2>
          <p className="text-slate-600 text-center mb-12 max-w-2xl mx-auto">
            A comprehensive leadership development experience built on four pillars
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div key={index} className="group relative bg-white rounded-2xl p-8 shadow-lg border border-slate-100 hover:shadow-xl transition-shadow duration-300">
                  <div className={`absolute top-0 left-8 right-8 h-1 bg-gradient-to-r ${feature.gradient} rounded-full`} />
                  <div className={`w-14 h-14 bg-gradient-to-br ${feature.gradient} rounded-xl flex items-center justify-center mb-6`}>
                    <Icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-800 mb-3">{feature.title}</h3>
                  <p className="text-slate-600">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Curriculum */}
      <section className="py-16 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <ClipboardList className="w-12 h-12 text-amber-400 mb-6" />
              <h2 className="text-3xl font-bold mb-4">Curriculum</h2>
              <p className="text-slate-300 leading-relaxed mb-8">
                Six modules grounded in the competencies emerging leaders need most:
              </p>
              <div className="space-y-3">
                {curriculum.map((module, i) => (
                  <div key={i} className="flex items-center gap-3 bg-white/10 backdrop-blur-sm rounded-xl px-5 py-3 border border-white/10">
                    <div className="w-8 h-8 bg-amber-500 rounded-lg flex items-center justify-center text-sm font-bold shrink-0">
                      {i + 1}
                    </div>
                    <span className="text-white/90">{module}</span>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <UserCheck className="w-12 h-12 text-amber-400 mb-6" />
              <h2 className="text-3xl font-bold mb-4">Who the Program Serves</h2>
              <p className="text-slate-300 leading-relaxed mb-6">
                The program is designed for early childhood professionals likely to step into formal leadership within the next two to four years. Participants may work in classrooms, coaching, family engagement, operations, advocacy, or program development across community-based and center-based settings.
              </p>
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
                <p className="text-amber-200 italic leading-relaxed text-sm">
                  In keeping with the legacy of Maurice Sykes, ideal participants bring a deep commitment to equity, advocacy through action, a belief in the role of lived experience in transforming systems, and an orientation toward servant leadership.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Promo Video Section */}
      <section className="py-16 bg-slate-900">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <div className="w-16 h-16 bg-amber-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <Play className="w-8 h-8 text-amber-400" />
            </div>
            <h2 className="text-3xl font-bold text-white mb-4">Watch Our Promo Video</h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              See how the Emerging Leaders Program is transforming early childhood education leadership in DC
            </p>
          </div>
          
          <div className="relative rounded-2xl overflow-hidden shadow-2xl bg-black">
            <video
              controls
              className="w-full h-auto"
              preload="metadata"
            >
              <source 
                src="https://customer-assets.emergentagent.com/job_early-learn-dc/artifacts/c4t1jvgm_DCELC_ALAA%20PROMO_FULL%20RECAP%20VIDEO_Rough%20Cut.mp4" 
                type="video/mp4" 
              />
              <track
                kind="captions"
                src="/captions/alaa-recap.vtt"
                srcLang="en"
                label="English"
                default
              />
              Your browser does not support the video tag.
            </video>
          </div>
        </div>
      </section>

      {/* Partnership */}
      <section className="py-16 bg-gradient-to-br from-amber-50 to-orange-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Handshake className="w-12 h-12 text-amber-600 mx-auto mb-4" />
            <h2 className="text-3xl font-bold text-slate-800 mb-4">A Partnership Built for Impact</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-amber-100">
              <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center mb-4">
                <Award className="w-6 h-6 text-amber-600" />
              </div>
              <h3 className="text-xl font-bold text-slate-800 mb-3">Educare Washington, DC</h3>
              <p className="text-slate-600 leading-relaxed">
                Brings national reputation for high-quality, data-driven early childhood practice, along with professional development infrastructure and evaluation support.
              </p>
            </div>
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-amber-100">
              <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center mb-4">
                <Sparkles className="w-6 h-6 text-orange-600" />
              </div>
              <h3 className="text-xl font-bold text-slate-800 mb-3">DC Early Learning Collaborative</h3>
              <p className="text-slate-600 leading-relaxed">
                Brings advocacy leadership, a wide field network, and the ability to position leadership development as a defining part of the early childhood ecosystem.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Apply CTA */}
      <section className="py-20 bg-gradient-to-br from-amber-600 via-orange-600 to-rose-700 relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-0 right-0 w-96 h-96 bg-white/10 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-80 h-80 bg-amber-300/10 rounded-full blur-3xl" />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <Calendar className="w-8 h-8 text-white" />
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">Apply Now — Pilot Cohort Launching June 2026</h2>
          <p className="text-amber-100 text-lg mb-3 max-w-2xl mx-auto">
            The Maurice Sykes Emerging Leaders Program launches the first pilot cohort in June 2026.
          </p>
          <div className="flex items-center justify-center gap-2 text-white/90 mb-8">
            <Clock className="w-5 h-5" />
            <p className="font-semibold">Application closes June 5th at 11:59 PM</p>
          </div>
          <div className="mb-6">
            <Link
              to="/programs/mselp/ideal-candidate"
              className="inline-flex items-center gap-2 text-white bg-white/20 hover:bg-white/30 backdrop-blur-sm px-6 py-3 rounded-full font-semibold transition-all duration-300 hover:scale-105"
              data-testid="alaa-ideal-candidate-link"
            >
              <UserCheck className="w-5 h-5" />
              Who is the Ideal Candidate?
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <Button
            asChild
            size="lg"
            className="bg-white text-amber-700 hover:bg-amber-50 px-10 py-6 text-lg font-bold shadow-2xl transition-all duration-300 hover:scale-105 group"
            data-testid="alaa-apply-btn"
          >
            <a href="https://form.jotform.com/261314658584060" target="_blank" rel="noopener noreferrer">
              Apply to the Program
              <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </a>
          </Button>
          <p className="text-amber-200 text-sm mt-8">
            To learn more about participation, partnership, or supporting the program's growth, contact{' '}
            <a href="mailto:admin@dcelc.org" className="underline hover:text-white">admin@dcelc.org</a>
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-slate-800 mb-6">Questions About the Emerging Leaders Program?</h2>
          <p className="text-lg text-slate-600 mb-8">
            Contact us to learn more about the program and application process
          </p>
          <Button
            onClick={() => window.location.href = '/#contact'}
            variant="outline"
            className="border-amber-300 text-amber-700 hover:bg-amber-50 px-8 py-6 text-lg"
          >
            Contact Us
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ALAA;
